// src/pages/FamilyOverview.tsx
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaChild,
  FaHome,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";

import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";

export default function FamilyOverview() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canViewFamiliesLegacy = useCan(
    "families",
    "read"
  );
  const canViewFamiliesDirect = useCan(
    "families",
    "read"
  );
  const canListFamiliesDirect = useCan(
    "families",
    "list"
  );

  const canViewFamilies =
    canViewFamiliesLegacy ||
    canViewFamiliesDirect ||
    canListFamiliesDirect;

  const canViewParentsDirect = useCan(
    "family.parents",
    "read"
  );
  const canListParentsDirect = useCan(
    "family.parents",
    "list"
  );
  const canViewParents =
    canViewParentsDirect ||
    canListParentsDirect ||
    canViewFamilies;

  const canViewChildrenDirect = useCan(
    "family.children",
    "read"
  );
  const canListChildrenDirect = useCan(
    "family.children",
    "list"
  );
  const canViewChildren =
    canViewChildrenDirect ||
    canListChildrenDirect;

  const visibleSections = [
    canViewFamilies,
    canViewParents,
    canViewChildren,
  ].filter(Boolean).length;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaHome}
        eyebrow={t("family.overview.eyebrow", {
          defaultValue: "Family",
        })}
        title={t("family.overview.title", {
          defaultValue: "Family management",
        })}
        description={t("family.overview.subtitle", {
          defaultValue:
            "Manage families, parents and children from one central place.",
        })}
      />

      {visibleSections > 0 ? (
        <LdsOverviewSection
          title={t(
            "family.overview.sections_title",
            { defaultValue: "Management sections" }
          )}
          description={t(
            "family.overview.sections_description",
            {
              defaultValue:
                "{{count}} sections are available.",
              count: visibleSections,
            }
          )}
        >
          <LdsOverviewGrid className="xl:grid-cols-3">
            {canViewFamilies ? (
              <LdsOverviewCard
                icon={FaUsers}
                eyebrow={t(
                  "family.overview.cards.family.eyebrow",
                  { defaultValue: "Households" }
                )}
                title={t(
                  "family.overview.manage_families",
                  { defaultValue: "Families" }
                )}
                description={t(
                  "family.overview.description",
                  {
                    defaultValue:
                      "View family composition, relationships and linked members.",
                  }
                )}
                actionLabel={t(
                  "family.overview.open_families",
                  { defaultValue: "Open families" }
                )}
                onClick={() =>
                  navigate("/family/families")
                }
                animationIndex={0}
              />
            ) : null}

            {canViewParents ? (
              <LdsOverviewCard
                icon={FaUserFriends}
                eyebrow={t(
                  "family.overview.cards.parents.eyebrow",
                  { defaultValue: "Guardians" }
                )}
                title={t(
                  "family.overview.manage_parents",
                  { defaultValue: "Parents" }
                )}
                description={t(
                  "family.overview.parents_description",
                  {
                    defaultValue:
                      "View and manage parent and guardian records.",
                  }
                )}
                actionLabel={t(
                  "family.overview.open_parents",
                  { defaultValue: "Open parents" }
                )}
                onClick={() =>
                  navigate("/family/parents")
                }
                animationIndex={1}
              />
            ) : null}

            {canViewChildren ? (
              <LdsOverviewCard
                icon={FaChild}
                eyebrow={t(
                  "family.overview.cards.children.eyebrow",
                  { defaultValue: "Students" }
                )}
                title={t(
                  "family.overview.manage_children",
                  { defaultValue: "Children" }
                )}
                description={t(
                  "family.overview.children_description",
                  {
                    defaultValue:
                      "View and manage child records and family links.",
                  }
                )}
                actionLabel={t(
                  "family.overview.open_children",
                  { defaultValue: "Open children" }
                )}
                onClick={() =>
                  navigate("/family/children")
                }
                animationIndex={2}
              />
            ) : null}
          </LdsOverviewGrid>
        </LdsOverviewSection>
      ) : (
        <LdsOverviewEmpty>
          {t("family.overview.empty", {
            defaultValue:
              "No family sections are available with your current permissions.",
          })}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
}
